import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComposantEcran } from '../../app/ecrans.js';
import { Lien } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS } from '../../app/styles.js';
import {
  creerSessionAppel,
  libelleBandeauAppel,
  messageARepriser,
  messageConsentement,
  messageDifferees,
  transportSync,
  type EtatAppel,
  type EtatConsentements,
  type SessionAppel,
} from './appel.js';
import { suivreAppel } from './appel-en-cours.js';
import {
  CE_QU_ELLE_DIT,
  CHAMPS_TEXTE_APPEL,
  DELAI_FRAPPE_MS,
  QUESTIONS_ENTRETIEN_INITIAL,
  REFUS_NUMERO,
  SITUATIONS_MATRIMONIALES,
  type GroupeChoix,
} from './donnees.js';
import { messageErreur } from './erreurs.js';
import { obtenirServices } from './services.js';
import { rejouer, type ConsentementAEnregistrer, type PersonneEquipe } from './dependances.js';
import { AUCUN_APPEL, ROUTE_CONSENTEMENT } from './donnees.js';

export interface PropsVueAppel {
  nom: string;
  contexte: string;
  chrono: string;
  enLigne: boolean;
  enAttente: number;
  saisies: Readonly<Record<string, string | boolean>>;
  refusNumero: boolean;
  onSaisie: (champ: string, valeur: string | boolean) => void;
  /** Frappe dans un champ texte (journalisée après un court délai, ou à la sortie du champ). Absent : `onSaisie`. */
  onFrappe?: (champ: string, valeur: string) => void;
  onRefus: (refus: boolean) => void;
  onTerminer: () => void;
  termine?: boolean;
  /** Libellés des saisies que le serveur a refusées (rejet ou conflit) : jamais présentées comme enregistrées. */
  elementsARepriser?: readonly string[];
  /** Éléments gardés sur l'appareil, le serveur n'en connaissant pas encore le type. */
  differees?: number;
  /** Refus de partage du numéro : gardé en attente du réseau, ou à reprendre (refusé par le serveur). */
  consentements?: EtatConsentements;
  onReprendreConsentement?: () => void;
  erreur?: string | null;
  titre?: string;
}

const styleChoix = (actif: boolean) =>
  ({
    minHeight: CIBLE_MIN,
    minWidth: CIBLE_MIN,
    padding: '0 16px',
    borderRadius: 8,
    border: `1px solid ${COULEURS.marine}`,
    background: actif ? COULEURS.marine : COULEURS.blanc,
    color: actif ? COULEURS.blanc : COULEURS.marine,
    font: 'inherit',
    cursor: 'pointer',
  }) as const;

const styleChamp = {
  minHeight: CIBLE_MIN,
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  font: 'inherit',
  boxSizing: 'border-box',
  width: '100%',
} as const;

export function VueAppel({
  nom,
  contexte,
  chrono,
  enLigne,
  enAttente,
  saisies,
  refusNumero,
  onSaisie,
  onFrappe,
  onRefus,
  onTerminer,
  termine,
  elementsARepriser = [],
  differees = 0,
  consentements = { enAttente: 0, aReprendre: false },
  onReprendreConsentement,
  erreur,
  titre,
}: PropsVueAppel) {
  const messageRefus = messageConsentement(consentements);
  const bandeau = libelleBandeauAppel(enLigne, enAttente);
  const groupe = (g: GroupeChoix) => (
    <fieldset key={g.champ} style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
      <legend style={{ fontWeight: 600, marginBottom: 8 }}>{g.titre}</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {g.choix.map((c) => (
          <button
            key={c.code}
            type="button"
            aria-pressed={saisies[g.champ] === c.code}
            style={styleChoix(saisies[g.champ] === c.code)}
            onClick={() => onSaisie(g.champ, c.code)}
          >
            {c.libelle}
          </button>
        ))}
      </div>
    </fieldset>
  );
  return (
    <section data-ecran="M02" aria-labelledby="titre-appel" style={{ color: COULEURS.marineFonce }}>
      {bandeau ? (
        <div
          role="status"
          aria-live="polite"
          data-indicateur="reseau"
          style={{ padding: '8px 16px', background: COULEURS.ambreFond, color: COULEURS.ambre, lineHeight: 1.5, marginBottom: 12 }}
        >
          {bandeau}
        </div>
      ) : null}
      {titre ? <p style={{ margin: 0, color: COULEURS.ardoise }}>{titre}</p> : null}
      <p style={{ margin: 0, color: COULEURS.ardoise }}>{`Appel en cours, ${chrono}, ${contexte}`}</p>
      <h1 id="titre-appel" style={{ color: COULEURS.marine, fontSize: 28, margin: '4px 0 16px' }}>
        {nom}
      </h1>
      {erreur ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      {elementsARepriser.length > 0 ? (
        <p role="alert" data-etat="a_reprendre" style={{ color: COULEURS.rouge, lineHeight: 1.5 }}>
          {messageARepriser(elementsARepriser)}
        </p>
      ) : null}
      {differees > 0 ? (
        <p role="status" data-etat="differees" style={{ color: COULEURS.ambre, lineHeight: 1.5 }}>
          {messageDifferees(differees)}
        </p>
      ) : null}
      {messageRefus ? (
        <div
          role={consentements.aReprendre ? 'alert' : 'status'}
          data-etat={consentements.aReprendre ? 'consentement_a_reprendre' : 'consentement_en_attente'}
          style={{ color: consentements.aReprendre ? COULEURS.rouge : COULEURS.ambre, lineHeight: 1.5 }}
        >
          <p style={{ margin: '0 0 8px' }}>{messageRefus}</p>
          {consentements.aReprendre && onReprendreConsentement ? (
            <button type="button" style={styleChoix(false)} onClick={onReprendreConsentement}>
              Réessayer l’enregistrement
            </button>
          ) : null}
        </div>
      ) : null}
      {termine ? (
        enAttente === 0 && elementsARepriser.length === 0 && differees === 0 && consentements.enAttente === 0 ? (
          <p role="status">Appel terminé. Le compte rendu est en brouillon : relisez-le avant tout envoi. Cabinet MAAT</p>
        ) : (
          <p role="status" data-etat="brouillon">
            Le compte rendu est en brouillon : relisez-le avant tout envoi. Des saisies de cet appel restent à transmettre au serveur.
            Cabinet MAAT
          </p>
        )
      ) : null}
      <h2 style={{ fontSize: 20 }}>À obtenir pendant l’appel</h2>
      {CHAMPS_TEXTE_APPEL.map((c) => (
        <label key={c.champ} style={{ display: 'block', marginBottom: 12 }}>
          {c.libelle}
          <input
            type="text"
            value={String(saisies[c.champ] ?? '')}
            style={styleChamp}
            onChange={(e) => (onFrappe ?? onSaisie)(c.champ, e.target.value)}
            onBlur={(e) => onSaisie(c.champ, e.target.value)}
          />
        </label>
      ))}
      {groupe(SITUATIONS_MATRIMONIALES)}
      {groupe(CE_QU_ELLE_DIT)}
      <label style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: CIBLE_MIN, marginBottom: 16 }}>
        <input
          type="checkbox"
          checked={refusNumero}
          onChange={(e) => onRefus(e.target.checked)}
          style={{ width: CIBLE_MIN, height: CIBLE_MIN }}
        />
        {REFUS_NUMERO}
      </label>
      <h2 style={{ fontSize: 20 }}>Entretien initial</h2>
      {QUESTIONS_ENTRETIEN_INITIAL.map((q) =>
        q.options ? (
          groupe({ champ: `entretien.${q.code}`, titre: q.question, choix: q.options })
        ) : (
          <label key={q.code} style={{ display: 'block', marginBottom: 12 }}>
            {q.question}
            <input
              type="text"
              value={String(saisies[`entretien.${q.code}`] ?? '')}
              style={styleChamp}
              onChange={(e) => (onFrappe ?? onSaisie)(`entretien.${q.code}`, e.target.value)}
              onBlur={(e) => onSaisie(`entretien.${q.code}`, e.target.value)}
            />
          </label>
        ),
      )}
      <button
        type="button"
        disabled={termine}
        style={{ ...styleChoix(true), background: COULEURS.boutonPrincipal, border: 'none', width: '100%', marginTop: 16 }}
        onClick={onTerminer}
      >
        Terminer l’appel et préparer le compte rendu
      </button>
    </section>
  );
}

const chrono = (secondes: number): string =>
  `${String(Math.floor(secondes / 60)).padStart(2, '0')}:${String(secondes % 60).padStart(2, '0')}`;

const lireParametres = (): URLSearchParams => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);

/** Sans personne dans l'adresse, aucun appel n'est en cours : on le dit et on renvoie vers la file du jour (constat du 26/10). */
function AucunAppel({ titre }: { titre: string }) {
  return (
    <section data-ecran="M02" aria-labelledby="titre-aucun-appel" style={{ color: COULEURS.marineFonce }}>
      <p style={{ margin: 0, color: COULEURS.ardoise }}>{titre}</p>
      <h1 id="titre-aucun-appel" style={{ color: COULEURS.marine, fontSize: 28, margin: '4px 0 16px' }}>
        {AUCUN_APPEL.titre}
      </h1>
      <p style={{ lineHeight: 1.5 }}>{AUCUN_APPEL.texte}</p>
      <Lien
        vers="/file-du-jour"
        enfants={AUCUN_APPEL.lien}
        style={{ display: 'inline-flex', alignItems: 'center', minHeight: CIBLE_MIN }}
      />
    </section>
  );
}

export const Appel: ComposantEcran = (props) =>
  lireParametres().get('personne') ? <AppelEnCours {...props} /> : <AucunAppel titre={props.ecran.titre} />;

const AppelEnCours: ComposantEcran = ({ ecran }) => {
  const services = obtenirServices();
  const [enLigne, setEnLigne] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [etat, setEtat] = useState<EtatAppel>({
    enAttente: 0,
    aReprendre: [],
    differees: 0,
    consentements: { enAttente: 0, aReprendre: false },
  });
  const [personne, setPersonne] = useState<PersonneEquipe | null>(null);
  const [saisies, setSaisies] = useState<Record<string, string | boolean>>({});
  const [refusNumero, setRefusNumero] = useState(false);
  const [secondes, setSecondes] = useState(0);
  const [termine, setTermine] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const parametres = useMemo(lireParametres, []);
  const [agentId, setAgentId] = useState('');

  // Un appel est en cours tant que l'écran est ouvert : la coquille retient alors les pop-ups BETA.
  useEffect(() => suivreAppel(), []);
  useEffect(() => {
    const id = setInterval(() => setSecondes((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let actif = true;
    const personId = parametres.get('personne');
    services.client
      .appeler<{ user: { id: string } }>('auth.moi')
      .then((s) => actif && setAgentId(s.user.id))
      .catch(() => undefined);
    if (personId) {
      services.client
        .appeler<PersonneEquipe>('genealogie.personne.lire', { parametres: { personId } })
        .then((p) => actif && setPersonne(p))
        .catch((e: unknown) => actif && setErreur(messageErreur(e)));
    }
    return () => {
      actif = false;
    };
  }, [services, parametres]);

  const session: SessionAppel | null = useMemo(() => {
    const personId = parametres.get('personne');
    const dossierId = personne?.dossierId ?? parametres.get('dossier');
    if (!personId || !dossierId) return null;
    return creerSessionAppel(
      {
        journal: services.journal,
        differe: services.differe,
        estSynchronisee: services.estSynchronisee,
        rejouer: () => rejouer({ journal: services.journal, transport: transportSync(services.transport), deviceId: services.deviceId }),
        // Refus de partage du numéro : route du contrat, audit `consentement.enregistrement` porté par la route.
        enregistrerConsentement: (personId: string, corps: ConsentementAEnregistrer) =>
          services.client.appeler(ROUTE_CONSENTEMENT, { parametres: { personId }, corps }),
        // Défaut assistante-01 : M02 enregistre l'appel par les routes directes du contrat, jamais par sync.push (501 par décision).
        envoyer: {
          compteRendu: (dossierId, corps) => services.client.appeler('comptesRendus.creer', { parametres: { dossierId }, corps }),
          tache: (tacheId, version, corps) =>
            services.client.appeler('taches.modifier', { parametres: { tacheId }, corps, versionLigne: version }),
          // Arbitrage du 26/10 : profession et situation sur la fiche (If-Match), adresse en coordonnée chiffrée.
          personne: (personId, version, corps) =>
            services.client.appeler('genealogie.personne.modifier', { parametres: { personId }, corps, versionLigne: version }),
          coordonnee: (personId, corps) => services.client.appeler('genealogie.coordonnees.ajouter', { parametres: { personId }, corps }),
        },
        enLigne: () => (typeof navigator === 'undefined' ? true : navigator.onLine),
        agentId,
        horloge: services.horloge,
        genererId: services.genererId,
      },
      {
        personId,
        dossierId,
        versionPersonne: personne?.rowVersion ?? 0,
        ...(parametres.get('tache')
          ? { tacheId: parametres.get('tache') as string, versionTache: Number(parametres.get('version') ?? 0) }
          : {}),
      },
    );
  }, [services, parametres, personne, agentId]);

  const rafraichir = useCallback(() => {
    void session?.etat().then(setEtat);
  }, [session]);

  useEffect(() => {
    const dedans = () => {
      setEnLigne(true);
      void session?.reprendreReseau().then(rafraichir);
    };
    const dehors = () => setEnLigne(false);
    window.addEventListener('online', dedans);
    window.addEventListener('offline', dehors);
    rafraichir();
    return () => {
      window.removeEventListener('online', dedans);
      window.removeEventListener('offline', dehors);
    };
  }, [session, rafraichir]);

  // Valeurs courantes, session courante et dernières valeurs journalisées : lues par les minuteries et le démontage.
  const saisiesCourantes = useRef<Record<string, string | boolean>>({});
  const sessionCourante = useRef<SessionAppel | null>(null);
  sessionCourante.current = session;
  const journalisees = useRef(new Map<string, string | boolean>());
  const minuteries = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const journaliser = useCallback(
    (champ: string, valeur: string | boolean) => {
      const courante = sessionCourante.current;
      if (!courante || journalisees.current.get(champ) === valeur) return;
      journalisees.current.set(champ, valeur);
      void courante
        .saisir(champ, valeur)
        .then(rafraichir)
        .catch((e: unknown) => setErreur(messageErreur(e)));
    },
    [rafraichir],
  );

  /** Journalise tout de suite la valeur d'un champ texte en attente (sortie du champ, fin d'appel, démontage). */
  const vider = useCallback(
    (champ: string) => {
      const m = minuteries.current.get(champ);
      if (m !== undefined) clearTimeout(m);
      minuteries.current.delete(champ);
      const valeur = saisiesCourantes.current[champ];
      // Un champ vidé n'entre au journal que s'il avait une valeur : l'effacement est alors une saisie (ARB-APPEL-09).
      if (typeof valeur === 'string' && (valeur !== '' || (journalisees.current.get(champ) ?? '') !== '')) journaliser(champ, valeur);
    },
    [journaliser],
  );
  const viderTout = useCallback(() => {
    for (const champ of [...minuteries.current.keys()]) vider(champ);
  }, [vider]);

  const poser = (champ: string, valeur: string | boolean) => {
    saisiesCourantes.current = { ...saisiesCourantes.current, [champ]: valeur };
    setSaisies(saisiesCourantes.current);
  };

  const frapper = (champ: string, valeur: string) => {
    poser(champ, valeur);
    const m = minuteries.current.get(champ);
    if (m !== undefined) clearTimeout(m);
    minuteries.current.set(
      champ,
      setTimeout(() => vider(champ), DELAI_FRAPPE_MS),
    );
  };

  const saisir = (champ: string, valeur: string | boolean) => {
    poser(champ, valeur);
    if (typeof valeur === 'string') vider(champ);
    else journaliser(champ, valeur);
  };

  // Interruption (l'écran se ferme en pleine frappe) : ce qui est tapé entre dans le journal avant le démontage.
  useEffect(() => viderTout, [viderTout]);

  // Retour sur l'appel (même adresse, rechargement) : les champs reprennent le journal de cet appel.
  useEffect(() => {
    let actif = true;
    void session?.reprendreSaisies().then((reprises) => {
      if (!actif) return;
      for (const [champ, valeur] of Object.entries(reprises)) if (!journalisees.current.has(champ)) journalisees.current.set(champ, valeur);
      saisiesCourantes.current = { ...reprises, ...saisiesCourantes.current };
      setSaisies(saisiesCourantes.current);
    });
    return () => {
      actif = false;
    };
  }, [session]);

  const reprendreConsentement = () => {
    void session
      ?.reprendreConsentements()
      .then(setEtat)
      .catch((e: unknown) => setErreur(messageErreur(e)));
  };

  const terminer = () => {
    if (!session) return;
    setErreur(null);
    viderTout();
    session
      .terminer({ refusNumero })
      .then(() => {
        setTermine(true);
        rafraichir();
      })
      .catch((e: unknown) => setErreur(messageErreur(e)));
  };

  return (
    <VueAppel
      nom={personne ? `${personne.firstNames.split(' ')[0] ?? ''} ${personne.lastName}`.trim() : 'Chargement de la fiche…'}
      contexte={personne?.relationLabel ?? ''}
      chrono={chrono(secondes)}
      enLigne={enLigne}
      enAttente={etat.enAttente}
      saisies={saisies}
      refusNumero={refusNumero}
      onSaisie={saisir}
      onFrappe={frapper}
      onRefus={setRefusNumero}
      onTerminer={terminer}
      termine={termine}
      elementsARepriser={etat.aReprendre}
      differees={etat.differees}
      consentements={etat.consentements}
      onReprendreConsentement={reprendreConsentement}
      erreur={erreur}
      titre={ecran.titre}
    />
  );
};
